import { Component, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { firstValueFrom, Observable, of } from 'rxjs';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { map } from 'rxjs/operators';
import { ModuleEntity } from '../module.entity';
import { ModuleEntityService } from '../services/module.entity.service';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';

export interface ReorderDialogData {
  module: ModuleEntity
  consumerId: string | null
  selectedCanton: string | null
}

export interface ReorderDialogResult {
  afterModuleId: string | null
}

@Component({
  selector: 'pixl-reorder-dialog-module',
  templateUrl: './reorder-dialog-module.component.html',
  styleUrl: './reorder-dialog-module.component.scss',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatRadioModule,
    FormsModule,
    TranslocoModule,
    MultiComboEntitySearchComponent
  ]
})
export class ReorderDialogModuleComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<ReorderDialogModuleComponent>);
  public data: ReorderDialogData = inject(MAT_DIALOG_DATA);
  private store = inject(Store);
  private moduleService = inject(ModuleEntityService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  modules$!: Observable<ModuleEntity[]>;
  selectedModule: ModuleEntity[] = [];
  placement: 'after' | 'first' = 'after';

  private currentPage = 1;
  private pageSize = 50;
  searchQuery: string | null = null;

  ngOnInit(): void {
    this.loadModules();
  }

  private async loadModules(): Promise<void> {
    const query = this.buildQuery();
    await this.moduleService.ensureLoaded(query);

    this.modules$ = this.store.select(this.moduleService.allByQuery(query)).pipe(
      map((modules: ModuleEntity[]) => this.filterModules(modules))
    );
  }

  private filterModules(modules: ModuleEntity[]): ModuleEntity[] {
    let filtered = modules.filter(m => m.id !== this.data.module.id);

    if (this.data.selectedCanton) {
      const canton = this.data.selectedCanton;
      filtered = filtered.filter(m => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const kantone = (m.consumerData as any)?.kantone;
        return Array.isArray(kantone) && kantone.some((k: { name: string }) => k.name === canton);
      });
    }

    if (this.data.selectedCanton) {
      const canton = this.data.selectedCanton;
      filtered = filtered.sort((a, b) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orderA = (a.order as any)?.[canton] ?? '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orderB = (b.order as any)?.[canton] ?? '';
        return orderA.localeCompare(orderB);
      });
    }

    return filtered;
  }

  private buildQuery(): Record<string, string | number> {
    const query: Record<string, string | number> = {
      'page[size]': this.pageSize,
      'page[number]': this.currentPage,
      'sort': 'title',
      'include': 'consumer',
    };

    if (this.data.consumerId) {
      query['filter[consumer][id]'] = this.data.consumerId;
    }

    if (this.searchQuery && this.searchQuery.length > 2) {
      query['filter[titleLike]'] = this.searchQuery;
    }

    return query;
  }

  receiveModuleData(selected: ModuleEntity[] | null): void {
    this.selectedModule = selected ?? [];
  }

  async receiveModuleQuery(query: string): Promise<void> {
    this.searchQuery = query?.trim() || null;
    this.currentPage = 1;

    if (this.searchQuery && this.searchQuery.length > 2) {
      const searchParams = this.buildQuery();
      await this.moduleService.ensureLoaded(searchParams);

      this.modules$ = this.store.select(this.moduleService.allByQuery(searchParams)).pipe(
        map((modules: ModuleEntity[]) => this.filterModules(modules))
      );
    }
    else {
      await this.loadModules();
    }
  }

  async loadMoreModules(event: { pageNumber: number, pageSize: number }): Promise<void> {
    if (!this.searchQuery) {
      this.currentPage = event.pageNumber;
      const query = this.buildQuery();
      await this.moduleService.ensureLoaded(query);

      const currentModules = await firstValueFrom(this.modules$ ?? of([]));

      this.modules$ = this.store.select(this.moduleService.allByQuery(query)).pipe(
        map((paginated: ModuleEntity[]) => {
          const merged = [
            ...this.selectedModule,
            ...currentModules,
            ...paginated,
          ].filter((m, index, self) => index === self.findIndex(s => s.id === m.id));
          return this.filterModules(merged);
        })
      );
    }
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  async confirm(): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(this.data.module.title),
            headerText: this.translocoService.translate('reorder_move_module'),
            afterNameText: this.translocoService.translate('reorder_really_move')
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    if (this.placement === 'first') {
      this.dialogRef.close({ afterModuleId: null } as ReorderDialogResult);
    }
    else if (this.selectedModule.length > 0) {
      this.dialogRef.close({ afterModuleId: this.selectedModule[0].id } as ReorderDialogResult);
    }
  }

  get canConfirm(): boolean {
    return this.placement === 'first' || this.selectedModule.length > 0;
  }
}
